import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, ImageIcon, Video, Film } from "lucide-react";
import { endpoints, API_BASE } from "@/lib/api";
import { chunkedUpload } from "@/lib/cfUpload";

const abs = (u) => (u && u.startsWith("/api/") ? `${API_BASE}${u}` : u);

// Reusable IMAGE or VIDEO (<=100MB) uploader. onChange receives { type, url, id }.
export function MediaUpload({ value, onChange, label = "Media", testid = "media", allowVideo = true }) {
  const imgRef = useRef(null);
  const vidRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const type = value?.type;
  const url = value?.url ? abs(value.url) : "";

  const onImage = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await endpoints.uploadImage(fd);
      onChange({ type: "image", url: abs(data.url) });
      toast.success("Image uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const onVideo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) return toast.error("Please choose a video file");
    if (file.size > 100 * 1024 * 1024) return toast.error("Video must be 100 MB or less");
    setBusy(true); setProgress(0);
    try {
      const { data: init } = await endpoints.mediaInit({ filename: file.name, contentType: file.type, size: file.size });
      await chunkedUpload(init.id, file, (id, blob) => endpoints.mediaChunk(id, blob), (p) => setProgress(p));
      const { data } = await endpoints.mediaFinalize(init.id);
      onChange({ type: "video", url: abs(data.url), id: data.id });
      toast.success("Video uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Video upload failed"); }
    finally { setBusy(false); setProgress(0); }
  };

  return (
    <div data-testid={`${testid}-wrap`}>
      <label className="text-xs text-slate-400 block mb-1">{label}</label>
      <div className="rounded-xl border border-[#273042] bg-[#0A0C10] p-3">
        {url ? (
          <div className="relative rounded-lg overflow-hidden bg-black aspect-video mb-3">
            {type === "video"
              ? <video src={url} controls playsInline className="w-full h-full object-contain" />
              : <img src={url} alt="" className="w-full h-full object-cover" />}
            <button type="button" onClick={() => onChange(null)} data-testid={`${testid}-remove`}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="aspect-video rounded-lg border border-dashed border-[#273042] flex flex-col items-center justify-center text-slate-600 mb-3">
            <Film className="w-7 h-7 mb-1" /><span className="text-xs">No media selected</span>
          </div>
        )}
        {busy && type !== "image" && progress > 0 && (
          <div className="mb-2"><div className="h-1.5 bg-[#1b2230] rounded-full overflow-hidden"><div className="h-full bg-[#E11D2A]" style={{ width: `${progress}%` }} /></div><p className="text-[10px] text-slate-500 mt-1">Uploading {progress}%</p></div>
        )}
        <div className="flex gap-2">
          <button type="button" onClick={() => imgRef.current?.click()} disabled={busy} data-testid={`${testid}-pick-image`}
            className="h-10 px-3 rounded-lg bg-white/5 border border-[#273042] text-slate-200 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Image
          </button>
          {allowVideo && (
            <button type="button" onClick={() => vidRef.current?.click()} disabled={busy} data-testid={`${testid}-pick-video`}
              className="h-10 px-3 rounded-lg bg-white/5 border border-[#273042] text-slate-200 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} Video (≤100MB)
            </button>
          )}
        </div>
        <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={onImage} />
        <input ref={vidRef} type="file" accept="video/*" className="hidden" onChange={onVideo} />
      </div>
    </div>
  );
}
